import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { Alert, Box, ButtonBase, Fade, IconButton, Modal, Stack, Typography } from '@mui/material';
import { getInternalQuotationPhotos } from '@/features/operations/api';
import type { QuotationPhotoWithUrl } from '@/features/operations/types';

/**
 * Photos for one request, fetched when its detail is opened.
 *
 * It only fetches while mounted, so the presigned URLs are minted only when somebody actually
 * looks and expire shortly after. A presigned URL is a bearer credential for that object, so
 * the fewer that exist the better (BR-062).
 */
export function QuotationPhotoGallery({
  token,
  quotationId,
  photoCount,
}: {
  token: string;
  quotationId: string;
  photoCount: number;
}) {
  const [photos, setPhotos] = useState<QuotationPhotoWithUrl[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!photoCount) {
      return;
    }

    let isActive = true;
    setIsLoading(true);

    getInternalQuotationPhotos(token, quotationId)
      .then((items) => {
        if (isActive) {
          setPhotos(items);
          setError(null);
        }
      })
      .catch((cause) => {
        if (isActive) {
          setError(cause instanceof Error ? cause.message : 'No pudimos cargar las fotos.');
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [token, quotationId, photoCount]);

  if (!photoCount) {
    return (
      <Typography variant="body2" color="text.secondary">
        Sin fotos adjuntas.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        FOTOS DEL EQUIPO
      </Typography>
      {isLoading ? <Typography variant="body2">Cargando fotos...</Typography> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={0.5}>
        {photos.map((photo, index) => (
          <ButtonBase
            key={photo.id}
            disableRipple
            onClick={() => setOpenIndex(index)}
            aria-label={`Ampliar foto ${index + 1} de ${photos.length}`}
            sx={{
              position: 'relative',
              width: 120,
              height: 120,
              borderRadius: 2,
              overflow: 'hidden',
              '& img': { transition: 'transform 250ms ease' },
              '& .zoom-hint': { opacity: 0, transition: 'opacity 200ms ease' },
              '&:hover img': { transform: 'scale(1.06)' },
              '&:hover .zoom-hint, &.Mui-focusVisible .zoom-hint': { opacity: 1 },
              // Only keyboard focus gets a ring; a mouse click leaves no frame behind.
              '&.Mui-focusVisible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
          >
            <Box
              component="img"
              src={photo.url}
              alt="Foto declarada del equipo"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <Box
              className="zoom-hint"
              aria-hidden
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'common.white',
                bgcolor: 'rgba(11,13,16,0.35)',
              }}
            >
              <ZoomInIcon />
            </Box>
          </ButtonBase>
        ))}
      </Stack>
      <PhotoViewer photos={photos} index={openIndex} onChange={setOpenIndex} />
    </Box>
  );
}

const overlayButtonSx = {
  color: 'common.white',
  bgcolor: 'rgba(255,255,255,0.08)',
  backdropFilter: 'blur(6px)',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
} as const;

/**
 * Full-screen view of the photos already fetched. It reuses the same presigned URLs instead of
 * minting new ones, so if the viewer stays open past their expiry the image stops loading; the
 * page has to be reopened to get fresh links.
 */
function PhotoViewer({
  photos,
  index,
  onChange,
}: {
  photos: QuotationPhotoWithUrl[];
  index: number | null;
  onChange: (index: number | null) => void;
}) {
  const [failedId, setFailedId] = useState<string | null>(null);
  // Keeps showing the last photo while the viewer fades out, instead of going blank first.
  const lastIndex = useRef(0);
  if (index !== null) {
    lastIndex.current = index;
  }
  const isOpen = index !== null && photos[index] !== undefined;
  const shownIndex = index ?? lastIndex.current;
  const photo = photos[shownIndex];
  const position = shownIndex + 1;
  const hasMany = photos.length > 1;

  const close = () => onChange(null);
  const step = (delta: number) => {
    if (index !== null && hasMany) {
      onChange((index + delta + photos.length) % photos.length);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      step(-1);
    } else if (event.key === 'ArrowRight') {
      step(1);
    }
  };

  // Clicking the dark area around the photo closes the viewer, like any lightbox. Clicks on the
  // photo or the controls have their own element as target and do not close it.
  const closeOnBackground = (event: MouseEvent) => {
    if (event.target === event.currentTarget) {
      close();
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={close}
      closeAfterTransition
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(8,10,12,0.92)', backdropFilter: 'blur(4px)' } },
      }}
    >
      <Fade in={isOpen}>
        <Box
          role="dialog"
          aria-modal="true"
          aria-label="Visor de fotos del equipo"
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            color: 'common.white',
            outline: 'none',
          }}
        >
          {photo ? (
            <>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: { xs: 2, sm: 3 }, py: 2 }}
              >
                <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 600 }}>
                  {hasMany ? `${position} / ${photos.length}` : 'Foto del equipo'}
                </Typography>
                <IconButton onClick={close} aria-label="Cerrar visor" sx={overlayButtonSx}>
                  <CloseIcon />
                </IconButton>
              </Stack>

              <Box
                onClick={closeOnBackground}
                sx={{
                  position: 'relative',
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: { xs: 2, sm: 10 },
                }}
              >
                {failedId === photo.id ? (
                  <Typography variant="body2" sx={{ maxWidth: 360, textAlign: 'center' }}>
                    No pudimos mostrar la foto. Es posible que el enlace haya expirado: volvé a
                    abrir la solicitud.
                  </Typography>
                ) : (
                  <Fade in key={photo.id} timeout={250}>
                    <Box
                      component="img"
                      src={photo.url}
                      alt={`Foto declarada del equipo, ampliada (${position} de ${photos.length})`}
                      onError={() => setFailedId(photo.id)}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: 2,
                        boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
                        userSelect: 'none',
                      }}
                    />
                  </Fade>
                )}

                {hasMany ? (
                  <>
                    <IconButton
                      onClick={() => step(-1)}
                      aria-label="Foto anterior"
                      sx={{ ...overlayButtonSx, position: 'absolute', left: { xs: 8, sm: 24 } }}
                    >
                      <ChevronLeftIcon fontSize="large" />
                    </IconButton>
                    <IconButton
                      onClick={() => step(1)}
                      aria-label="Foto siguiente"
                      sx={{ ...overlayButtonSx, position: 'absolute', right: { xs: 8, sm: 24 } }}
                    >
                      <ChevronRightIcon fontSize="large" />
                    </IconButton>
                  </>
                ) : null}
              </Box>

              {hasMany ? (
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="center"
                  sx={{ px: 2, py: 2, overflowX: 'auto' }}
                >
                  {photos.map((item, itemIndex) => {
                    const isCurrent = itemIndex === shownIndex;
                    return (
                      <ButtonBase
                        key={item.id}
                        disableRipple
                        onClick={() => onChange(itemIndex)}
                        aria-label={`Ver foto ${itemIndex + 1}`}
                        aria-current={isCurrent ? 'true' : undefined}
                        sx={{
                          flexShrink: 0,
                          width: 56,
                          height: 56,
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          opacity: isCurrent ? 1 : 0.45,
                          transform: isCurrent ? 'scale(1.05)' : 'none',
                          transition: 'opacity 200ms ease, transform 200ms ease',
                          '&:hover': { opacity: 1 },
                          '&.Mui-focusVisible': {
                            outline: '2px solid',
                            outlineColor: 'common.white',
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={item.url}
                          alt=""
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      </ButtonBase>
                    );
                  })}
                </Stack>
              ) : (
                <Box sx={{ height: 24 }} />
              )}
            </>
          ) : (
            <Box />
          )}
        </Box>
      </Fade>
    </Modal>
  );
}
